import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  IconButton,
  Button,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Grid,
  Card,
  CardContent,
  TablePagination,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Alert,
  LinearProgress
} from "@mui/material";
import { 
  Add, 
  Save, 
  Cancel, 
  Edit, 
  Delete, 
  Visibility,
  Close,
  CloudUpload,
  Download,
  Preview
} from "@mui/icons-material";
import axios from "axios";

const EducationData = () => {
  const [educations, setEducations] = useState([]);
  const [newEducation, setNewEducation] = useState({
    qualification: "",
    courseType: "",
    stream: "",
    startDate: "",
    endDate: "",
    collegeUniversity: "",
    grade: "",
    certificateFile: null,
    certificateName: "",
    certificateUrl: ""
  });
  const [editingId, setEditingId] = useState(null);
  const [showAddRow, setShowAddRow] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedEducation, setSelectedEducation] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  
  const fileInputRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  // Course Types
  const courseTypes = [
    "Full-time",
    "Part-time",
    "Distance",
    "Online",
    "Correspondence"
  ];

  // Qualifications
  const qualifications = [
    "High School",
    "Intermediate",
    "Diploma",
    "Bachelor's Degree",
    "Master's Degree",
    "PhD",
    "Post Graduate Diploma",
    "Certificate Course",
    "Other"
  ];

  const handleNewEducationChange = (field, value) => {
    setNewEducation(prev => ({ ...prev, [field]: value }));
  };

  const handleEducationChange = (id, field, value) => {
    setEducations(prev =>
      prev.map(edu =>
        edu.id === id ? { ...edu, [field]: value } : edu
      )
    );
  };

  const handleFileUpload = async (event, id = null) => {
    const file = event.target.files[0];
    if (!file) return;

    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert("File size should be less than 5MB");
      return;
    }

    // Check file type
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      alert("Only PDF, JPEG, and PNG files are allowed");
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      // Create FormData
      const formData = new FormData();
      formData.append('certificate', file);
      formData.append('fileName', file.name);
      
      // Simulate upload progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 200);

      // Upload file to server
      const response = await axios.post(
        "http://10.10.222.56:8283/education/uploadCertificate",
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setUploadProgress(percentCompleted);
          },
        }
      );

      clearInterval(progressInterval);
      setUploadProgress(100);

      if (response.data.success) {
        const certificateUrl = response.data.fileUrl;
        const certificateName = file.name;

        if (id) {
          // Update existing education record
          handleEducationChange(id, "certificateUrl", certificateUrl);
          handleEducationChange(id, "certificateName", certificateName);
          setTimeout(() => {
            setUploading(false);
            setUploadProgress(0);
            alert("Certificate uploaded successfully!");
          }, 500);
        } else {
          // For new education record
          setNewEducation(prev => ({
            ...prev,
            certificateFile: file,
            certificateUrl: certificateUrl,
            certificateName: certificateName
          }));
          setTimeout(() => {
            setUploading(false);
            setUploadProgress(0);
            alert("Certificate uploaded successfully!");
          }, 500);
        }
      } else {
        throw new Error(response.data.message);
      }
    } catch (error) {
      console.error("Error uploading certificate:", error);
      alert("Error uploading certificate. Please try again.");
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleRemoveCertificate = (id = null) => {
    if (id) {
      handleEducationChange(id, "certificateUrl", "");
      handleEducationChange(id, "certificateName", "");
    } else {
      setNewEducation(prev => ({
        ...prev,
        certificateFile: null,
        certificateUrl: "",
        certificateName: ""
      }));
    }
  };

  const handleDownloadCertificate = (certificateUrl, fileName) => {
    // Create a temporary link element
    const link = document.createElement('a');
    link.href = certificateUrl;
    link.download = fileName || 'certificate';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleAddNew = async () => {
    if (newEducation.qualification && newEducation.collegeUniversity) {
      try {
        const educationData = {
          ...newEducation,
          certificateFile: undefined // Remove file object before sending
        };

        const response = await axios.post(
          "http://10.10.222.56:8283/education/AddEducation",
          educationData
        );

        if (response.data.success) {
          setEducations([{ ...newEducation, id: Date.now() }, ...educations]);
          setNewEducation({
            qualification: "",
            courseType: "",
            stream: "",
            startDate: "",
            endDate: "",
            collegeUniversity: "",
            grade: "",
            certificateFile: null,
            certificateName: "",
            certificateUrl: ""
          });
          setShowAddRow(false);
          alert("Education record added successfully!");
        } else {
          console.error("Failed to add education record:", response.data.message);
          alert("Failed to add education record. Please try again.");
        }
      } catch (error) {
        console.error("Error adding education record:", error);
        alert("Error adding education record. Please check your connection.");
      }
    } else {
      alert("Qualification and College/University are required fields!");
    }
  };

  const handleEdit = (id) => {
    setEditingId(id);
  };

  const handleSave = async (id) => {
    const educationToUpdate = educations.find(edu => edu.id === id);
    if (educationToUpdate) {
      try {
        const updateData = { ...educationToUpdate };
        delete updateData.certificateFile;

        const response = await axios.put(
          `http://10.10.222.56:8283/education/UpdateEducation/${id}`,
          updateData
        );

        if (response.data.success) {
          setEditingId(null);
          alert("Education record updated successfully!");
        } else {
          console.error("Failed to update education record:", response.data.message);
          alert("Failed to update education record.");
        }
      } catch (error) {
        console.error("Error updating education record:", error);
        alert("Error updating education record.");
      }
    }
  };

  const handleCancel = () => {
    setEditingId(null);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this education record?")) {
      try {
        const response = await axios.delete(
          `http://10.10.222.56:8283/education/DeleteEducation/${id}`
        );

        if (response.data.success) {
          setEducations(prev => prev.filter(edu => edu.id !== id));
          alert("Education record deleted successfully!");
        } else {
          console.error("Failed to delete education record:", response.data.message);
          alert("Failed to delete education record.");
        }
      } catch (error) {
        console.error("Error deleting education record:", error);
        alert("Error deleting education record.");
      }
    }
  };

  const handleView = (education) => {
    setSelectedEducation(education);
    setViewDialogOpen(true);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    const fetchEducationRecords = async () => {
      try {
        const response = await axios.get("http://10.10.222.56:8283/education/GetEducationRecords/1");
        if (response.data.success) {
          setEducations(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching education records:", error);
        alert("Error fetching education records. Please check your connection.");
      }
    };

    fetchEducationRecords();
  }, []);

  // Responsive table header
  const tableHeaders = isMobile
    ? ["S.NO", "Qualification", "College/University", "Grade", "Certificate", "Actions"]
    : ["S.NO", "Qualification", "Course Type", "Stream", "Start Date", "End Date", "College/University", "Grade", "Certificate", "Actions"];

  // Mobile View - Card layout
  if (isMobile) {
    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" component="h2">
            Education Details
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setShowAddRow(true)}
            size="small"
          >
            Add Record
          </Button>
        </Box>

        {showAddRow && (
          <Card sx={{ mb: 2, bgcolor: '#f5f5f5' }}>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>
                Add  Education Record
              </Typography>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Qualification *</InputLabel>
                    <Select
                      value={newEducation.qualification}
                      onChange={(e) => handleNewEducationChange("qualification", e.target.value)}
                      label="Qualification *"
                    >
                      <MenuItem value=""><em>Select Qualification</em></MenuItem>
                      {qualifications.map((qual) => (
                        <MenuItem key={qual} value={qual}>{qual}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Course Type</InputLabel>
                    <Select
                      value={newEducation.courseType}
                      onChange={(e) => handleNewEducationChange("courseType", e.target.value)}
                      label="Course Type"
                    >
                      <MenuItem value=""><em>Select Course Type</em></MenuItem>
                      {courseTypes.map((type) => (
                        <MenuItem key={type} value={type}>{type}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Stream"
                    size="small"
                    value={newEducation.stream}
                    onChange={(e) => handleNewEducationChange("stream", e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Start Date"
                    type="date"
                    size="small"
                    value={newEducation.startDate}
                    onChange={(e) => handleNewEducationChange("startDate", e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="End Date"
                    type="date"
                    size="small"
                    value={newEducation.endDate}
                    onChange={(e) => handleNewEducationChange("endDate", e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="College/University *"
                    size="small"
                    value={newEducation.collegeUniversity}
                    onChange={(e) => handleNewEducationChange("collegeUniversity", e.target.value)}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Grade"
                    size="small"
                    value={newEducation.grade}
                    onChange={(e) => handleNewEducationChange("grade", e.target.value)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ border: '1px dashed #ccc', p: 2, borderRadius: 1 }}>
                    <Typography variant="body2" gutterBottom>
                      Certificate Upload
                    </Typography>
                    {newEducation.certificateName ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Chip
                          label={newEducation.certificateName}
                          onDelete={() => handleRemoveCertificate()}
                          size="small"
                        />
                        <IconButton
                          size="small"
                          onClick={() => handleDownloadCertificate(newEducation.certificateUrl, newEducation.certificateName)}
                        >
                          <Download fontSize="small" />
                        </IconButton>
                      </Box>
                    ) : (
                      <>
                        <input
                          type="file"
                          ref={fileInputRef}
                          onChange={(e) => handleFileUpload(e)}
                          accept=".pdf,.jpg,.jpeg,.png"
                          style={{ display: 'none' }}
                        />
                        <Button
                          variant="outlined"
                          startIcon={<CloudUpload />}
                          onClick={() => fileInputRef.current.click()}
                          size="small"
                          fullWidth
                        >
                          Upload Certificate
                        </Button>
                        <Typography variant="caption" color="text.secondary">
                          Max 5MB (PDF, JPG, PNG)
                        </Typography>
                      </>
                    )}
                    {uploading && (
                      <Box sx={{ mt: 1 }}>
                        <LinearProgress variant="determinate" value={uploadProgress} />
                        <Typography variant="caption">
                          Uploading: {uploadProgress}%
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                    <Button
                      variant="contained"
                      onClick={handleAddNew}
                      disabled={!newEducation.qualification || !newEducation.collegeUniversity}
                      size="small"
                      startIcon={<Save />}
                    >
                      Save
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => setShowAddRow(false)}
                      size="small"
                      startIcon={<Close />}
                    >
                      Cancel
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}

        {educations.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((edu, index) => (
          <Card key={edu.id} sx={{ mb: 1 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {edu.qualification}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {edu.collegeUniversity}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {page * rowsPerPage + index + 1}
                </Typography>
              </Box>

              <Grid container spacing={0.5}>
                <Grid item xs={6}>
                  <Typography variant="body2">
                    <strong>Stream:</strong> {edu.stream}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2">
                    <strong>Grade:</strong> {edu.grade}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2">
                    <strong>Start:</strong> {edu.startDate}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2">
                    <strong>End:</strong> {edu.endDate}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                    <Typography variant="body2">
                      <strong>Certificate:</strong>
                      {edu.certificateName ? (
                        <Chip
                          label={edu.certificateName}
                          size="small"
                          sx={{ ml: 1 }}
                        />
                      ) : (
                        <span style={{ marginLeft: '4px', color: '#666' }}>No certificate</span>
                      )}
                    </Typography>
                    {edu.certificateUrl && (
                      <IconButton
                        size="small"
                        onClick={() => handleDownloadCertificate(edu.certificateUrl, edu.certificateName)}
                      >
                        <Download fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                </Grid>
              </Grid>

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5, mt: 1 }}>
                <IconButton size="small" onClick={() => handleView(edu)}>
                  <Visibility fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={() => handleEdit(edu.id)}>
                  <Edit fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={() => handleDelete(edu.id)}>
                  <Delete fontSize="small" />
                </IconButton>
              </Box>
            </CardContent>
          </Card>
        ))}

        <TablePagination
          component="div"
          count={educations.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />

        {/* View Dialog for Mobile */}
        <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} fullWidth maxWidth="sm">
          <DialogTitle>Education Details</DialogTitle>
          <DialogContent>
            {selectedEducation && (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography><strong>Qualification:</strong> {selectedEducation.qualification}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography><strong>Course Type:</strong> {selectedEducation.courseType}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography><strong>Stream:</strong> {selectedEducation.stream}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography><strong>Start Date:</strong> {selectedEducation.startDate}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography><strong>End Date:</strong> {selectedEducation.endDate}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography><strong>College/University:</strong> {selectedEducation.collegeUniversity}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography><strong>Grade:</strong> {selectedEducation.grade}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography><strong>Certificate:</strong></Typography>
                  {selectedEducation.certificateUrl ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                      <Chip label={selectedEducation.certificateName} />
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<Download />}
                        onClick={() => handleDownloadCertificate(selectedEducation.certificateUrl, selectedEducation.certificateName)}
                      >
                        Download
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<Preview />}
                        onClick={() => window.open(selectedEducation.certificateUrl, '_blank')}
                      >
                        Preview
                      </Button>
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">No certificate uploaded</Typography>
                  )}
                </Grid>
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  // Tablet and Desktop View
  return (
    <Box>
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 2,
        flexDirection: { xs: 'column', sm: 'row' },
        gap: { xs: 1, sm: 0 }
      }}>
        <Typography variant="h5" component="h1">
          Education Details
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setShowAddRow(true)}
          sx={{ minWidth: { xs: '100%', sm: 'auto' } }}
        >
          Add  Education
        </Button>
      </Box>

      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: 400 }}>
          <Table stickyHeader size={isTablet ? "small" : "medium"}>
            <TableHead>
              <TableRow>
                {tableHeaders.map((header, index) => (
                  <TableCell
                    key={index}
                    align="center"
                    sx={{
                      fontWeight: "bold",
                      backgroundColor: theme.palette.primary.light,
                      color: theme.palette.primary.contrastText,
                      whiteSpace: 'nowrap',
                      padding: '8px'
                    }}
                  >
                    {header}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {showAddRow && (
                <TableRow sx={{ bgcolor: '#f9f9f9' }}>
                  <TableCell align="center" sx={{ padding: '8px' }}>-</TableCell>
                  <TableCell align="center" sx={{ padding: '8px' }}>
                    <FormControl fullWidth size="small">
                      <Select
                        value={newEducation.qualification}
                        onChange={(e) => handleNewEducationChange("qualification", e.target.value)}
                        displayEmpty
                      >
                        <MenuItem value=""><em>Select</em></MenuItem>
                        {qualifications.map((qual) => (
                          <MenuItem key={qual} value={qual}>{qual}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell align="center" sx={{ padding: '8px' }}>
                    <FormControl fullWidth size="small">
                      <Select
                        value={newEducation.courseType}
                        onChange={(e) => handleNewEducationChange("courseType", e.target.value)}
                        displayEmpty
                      >
                        <MenuItem value=""><em>Select</em></MenuItem>
                        {courseTypes.map((type) => (
                          <MenuItem key={type} value={type}>{type}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell align="center" sx={{ padding: '8px' }}>
                    <TextField
                      size="small"
                      fullWidth
                      value={newEducation.stream}
                      onChange={(e) => handleNewEducationChange("stream", e.target.value)}
                    />
                  </TableCell>
                  <TableCell align="center" sx={{ padding: '8px' }}>
                    <TextField
                      size="small"
                      type="date"
                      fullWidth
                      value={newEducation.startDate}
                      onChange={(e) => handleNewEducationChange("startDate", e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </TableCell>
                  <TableCell align="center" sx={{ padding: '8px' }}>
                    <TextField
                      size="small"
                      type="date"
                      fullWidth
                      value={newEducation.endDate}
                      onChange={(e) => handleNewEducationChange("endDate", e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </TableCell>
                  <TableCell align="center" sx={{ padding: '8px' }}>
                    <TextField
                      size="small"
                      fullWidth
                      value={newEducation.collegeUniversity}
                      onChange={(e) => handleNewEducationChange("collegeUniversity", e.target.value)}
                      required
                    />
                  </TableCell>
                  <TableCell align="center" sx={{ padding: '8px' }}>
                    <TextField
                      size="small"
                      fullWidth
                      value={newEducation.grade}
                      onChange={(e) => handleNewEducationChange("grade", e.target.value)}
                    />
                  </TableCell>
                  <TableCell align="center" sx={{ padding: '8px' }}>
                    <Box sx={{ minWidth: '150px' }}>
                      {newEducation.certificateName ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'center' }}>
                          <Chip
                            label={newEducation.certificateName}
                            onDelete={() => handleRemoveCertificate()}
                            size="small"
                          />
                          <IconButton
                            size="small"
                            onClick={() => handleDownloadCertificate(newEducation.certificateUrl, newEducation.certificateName)}
                          >
                            <Download fontSize="small" />
                          </IconButton>
                        </Box>
                      ) : (
                        <>
                          <input
                            type="file"
                            ref={fileInputRef}
                            onChange={(e) => handleFileUpload(e)}
                            accept=".pdf,.jpg,.jpeg,.png"
                            style={{ display: 'none' }}
                          />
                          <Button
                            variant="outlined"
                            startIcon={<CloudUpload />}
                            onClick={() => fileInputRef.current.click()}
                            size="small"
                            fullWidth
                          >
                            Upload
                          </Button>
                          {uploading && (
                            <LinearProgress variant="determinate" value={uploadProgress} sx={{ mt: 0.5 }} />
                          )}
                        </>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="center" sx={{ padding: '8px' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                      <IconButton
                        onClick={handleAddNew}
                        disabled={!newEducation.qualification || !newEducation.collegeUniversity}
                        color="primary"
                        size="small"
                      >
                        <Save />
                      </IconButton>
                      <IconButton
                        onClick={() => setShowAddRow(false)}
                        color="error"
                        size="small"
                      >
                        <Cancel />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              )}

              {educations.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((edu, index) => (
                <TableRow key={edu.id}>
                  <TableCell align="center" sx={{ padding: '8px' }}>{page * rowsPerPage + index + 1}</TableCell>
                  <TableCell align="center" sx={{ padding: '8px' }}>
                    {editingId === edu.id ? (
                      <FormControl fullWidth size="small">
                        <Select
                          value={edu.qualification}
                          onChange={(e) => handleEducationChange(edu.id, "qualification", e.target.value)}
                        >
                          {qualifications.map((qual) => (
                            <MenuItem key={qual} value={qual}>{qual}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : (
                      edu.qualification
                    )}
                  </TableCell>
                  <TableCell align="center" sx={{ padding: '8px' }}>
                    {editingId === edu.id ? (
                      <FormControl fullWidth size="small">
                        <Select
                          value={edu.courseType}
                          onChange={(e) => handleEducationChange(edu.id, "courseType", e.target.value)}
                        >
                          {courseTypes.map((type) => (
                            <MenuItem key={type} value={type}>{type}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : (
                      edu.courseType
                    )}
                  </TableCell>
                  <TableCell align="center" sx={{ padding: '8px' }}>
                    {editingId === edu.id ? (
                      <TextField
                        size="small"
                        fullWidth
                        value={edu.stream}
                        onChange={(e) => handleEducationChange(edu.id, "stream", e.target.value)}
                      />
                    ) : (
                      edu.stream
                    )}
                  </TableCell>
                  <TableCell align="center" sx={{ padding: '8px' }}>
                    {editingId === edu.id ? (
                      <TextField
                        size="small"
                        type="date"
                        fullWidth
                        value={edu.startDate}
                        onChange={(e) => handleEducationChange(edu.id, "startDate", e.target.value)}
                        InputLabelProps={{ shrink: true }}
                      />
                    ) : (
                      edu.startDate
                    )}
                  </TableCell>
                  <TableCell align="center" sx={{ padding: '8px' }}>
                    {editingId === edu.id ? (
                      <TextField
                        size="small"
                        type="date"
                        fullWidth
                        value={edu.endDate}
                        onChange={(e) => handleEducationChange(edu.id, "endDate", e.target.value)}
                        InputLabelProps={{ shrink: true }}
                      />
                    ) : (
                      edu.endDate
                    )}
                  </TableCell>
                  <TableCell align="center" sx={{ padding: '8px' }}>
                    {editingId === edu.id ? (
                      <TextField
                        size="small"
                        fullWidth
                        value={edu.collegeUniversity}
                        onChange={(e) => handleEducationChange(edu.id, "collegeUniversity", e.target.value)}
                      />
                    ) : (
                      <Typography variant="body2" sx={{ maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {edu.collegeUniversity}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center" sx={{ padding: '8px' }}>
                    {editingId === edu.id ? (
                      <TextField
                        size="small"
                        fullWidth
                        value={edu.grade}
                        onChange={(e) => handleEducationChange(edu.id, "grade", e.target.value)}
                      />
                    ) : (
                      edu.grade
                    )}
                  </TableCell>
                  <TableCell align="center" sx={{ padding: '8px' }}>
                    {editingId === edu.id ? (
                      <Box sx={{ minWidth: '150px' }}>
                        {edu.certificateName ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'center' }}>
                            <Chip
                              label={edu.certificateName}
                              onDelete={() => handleRemoveCertificate(edu.id)}
                              size="small"
                            />
                            <input
                              type="file"
                              ref={fileInputRef}
                              onChange={(e) => handleFileUpload(e, edu.id)}
                              accept=".pdf,.jpg,.jpeg,.png"
                              style={{ display: 'none' }}
                            />
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => fileInputRef.current.click()}
                            >
                              Change
                            </Button>
                          </Box>
                        ) : (
                          <>
                            <input
                              type="file"
                              ref={fileInputRef}
                              onChange={(e) => handleFileUpload(e, edu.id)}
                              accept=".pdf,.jpg,.jpeg,.png"
                              style={{ display: 'none' }}
                            />
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => fileInputRef.current.click()}
                              fullWidth
                            >
                              Upload
                            </Button>
                          </>
                        )}
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        {edu.certificateUrl ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'center' }}>
                            <Chip
                              label={edu.certificateName}
                              size="small"
                              onClick={() => handleDownloadCertificate(edu.certificateUrl, edu.certificateName)}
                            />
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <IconButton
                                size="small"
                                onClick={() => handleDownloadCertificate(edu.certificateUrl, edu.certificateName)}
                              >
                                <Download fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => window.open(edu.certificateUrl, '_blank')}
                              >
                                <Preview fontSize="small" />
                              </IconButton>
                            </Box>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">No certificate</Typography>
                        )}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell align="center" sx={{ padding: '8px' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                      {editingId === edu.id ? (
                        <>
                          <IconButton onClick={() => handleSave(edu.id)} size="small">
                            <Save color="primary" />
                          </IconButton>
                          <IconButton onClick={handleCancel} size="small">
                            <Cancel color="error" />
                          </IconButton>
                        </>
                      ) : (
                        <>
                          <IconButton onClick={() => handleView(edu)} size="small">
                            <Visibility color="info" />
                          </IconButton>
                          <IconButton onClick={() => handleEdit(edu.id)} size="small">
                            <Edit color="primary" />
                          </IconButton>
                          <IconButton onClick={() => handleDelete(edu.id)} size="small">
                            <Delete color="error" />
                          </IconButton>
                        </>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={educations.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* View Dialog for Tablet/Desktop */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Education Record Details</DialogTitle>
        <DialogContent>
          {selectedEducation && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1"><strong>Qualification:</strong> {selectedEducation.qualification}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1"><strong>Course Type:</strong> {selectedEducation.courseType}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1"><strong>Stream:</strong> {selectedEducation.stream}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1"><strong>Grade:</strong> {selectedEducation.grade}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1"><strong>Start Date:</strong> {selectedEducation.startDate}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1"><strong>End Date:</strong> {selectedEducation.endDate}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle1"><strong>College/University:</strong> {selectedEducation.collegeUniversity}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle1"><strong>Certificate:</strong></Typography>
                {selectedEducation.certificateUrl ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                    <Chip 
                      label={selectedEducation.certificateName} 
                      onClick={() => handleDownloadCertificate(selectedEducation.certificateUrl, selectedEducation.certificateName)}
                      clickable
                    />
                    <Button
                      variant="contained"
                      startIcon={<Download />}
                      onClick={() => handleDownloadCertificate(selectedEducation.certificateUrl, selectedEducation.certificateName)}
                      size="small"
                    >
                      Download
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<Preview />}
                      onClick={() => window.open(selectedEducation.certificateUrl, '_blank')}
                      size="small"
                    >
                      Preview
                    </Button>
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">No certificate uploaded</Typography>
                )}
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EducationData;